/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {getConfig} from 'pwa-kit-runtime/utils/ssr-config'
import React, {useCallback, useEffect, useState, useRef, useMemo} from 'react'
import {createPortal} from 'react-dom'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
// Components
import {Box, Skeleton, Flex, Container} from '@chakra-ui/react'
import {
    ProductQuickviewModal,
    useProductQuickviewModal
} from '../../components/product-quickview-modal'
import {unifyProduct, useUnifiedProduct} from '../../hooks/use-unified-product'
import lazyLoadable from '../../utils/lazy-loadable'
import PdpSubnavigation from './partials/pdp-subnavigation'
import AfterSales from './partials/after-sales'
// Hooks
import {GenericModal, useGenericModal} from '../../hooks/use-generic-modal'
import useEinstein from '../../commerce-api/hooks/useEinstein'
import CMSContent from '../../components/cms-content'
import ImageGallerySkeleton from '../../components/image-gallery/skeleton'
import {useHeader} from '../../hooks/use-header'
import {preloadCmsContent} from '../../components/cms-content/preload-cms-content'
import useOcapiSite from '../../commerce-api/hooks/useOcapiSite'
// Project Components
import EmotionalGallerySkeleton from '../../components/emotional-gallery/emotional-gallery-skeleton'
import ProductUSPSkeleton from './partials/product-usp-skeleton'
// Others/Utils
import {HTTPNotFound} from 'pwa-kit-react-sdk/ssr/universal/errors'
import {
    getMasterProductId,
    getPreselectedVariant,
    isProductMaster,
    isProductSet
} from '../../utils/product-utils'
// constant
import {MINIMUN_NUM_OF_RECOMMENDED_PRODUCTS, RECOMMENDED_ACCESSORIES_TYPE} from '../../constants'
import Cockpit from './partials/cockpit'
import ProductSetCockpit from './partials/product-set-cockpit'
import CockpitSkeleton from './partials/cockpit-skeleton'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import useTrackViewPDP from './hooks/use-track-view-pdp'
import useInteractionStudioPixel from '../../tracking/interaction-studio'
import useStructuredData from '../../hooks/use-structured-data'
import {isServer} from '../../utils/utils'
import useProductWithCustomerPromotions from '../../hooks/use-product-with-customer-promotions'
import Seo from '../../components/seo'
import {CachedProductsProvider} from '../../hooks/use-cached-products'
import {trackRRClickStars} from '../../tracking/analytics'
import {hasBVProductRating} from '../../components/bazaarvoice/utils'
import useEffectEvent from '../../hooks/use-effect-event'

// Dynamic modules - use ssr: false to prevent server side rendering
const fallback = <Skeleton height="25vh" width="100%" />
const ReviewOverview = lazyLoadable(() => import('../../components/bazaarvoice/review-overview'), {
    fallback,
    ssr: false
})
const ProductRecommendations = lazyLoadable(() => import('./partials/product-recommendations'), {
    fallback,
    ssr: false
})
const EmotionalGallery = lazyLoadable(() => import('../../components/emotional-gallery'), {
    fallback: <EmotionalGallerySkeleton />,
    ssr: false
})
const ProductUSP = lazyLoadable(() => import('./partials/product-usp'), {
    fallback: <ProductUSPSkeleton />,
    ssr: false
})
const ZoomModal = lazyLoadable(() => import('./partials/zoom-modal'), {
    fallback: <ImageGallerySkeleton />,
    ssr: false
})
const Specifications = lazyLoadable(
    () => import('../../pages/product-detail/partials/Specifications'),
    {
        fallback,
        ssr: false
    }
)
const AccessoryAdder = lazyLoadable(() => import('../../components/accessory-adder'), {
    fallback,
    ssr: false
})
const Breadcrumb = lazyLoadable(() => import('../../components/breadcrumb'), {
    fallback,
    ssr: false
})
const BackToTopLink = lazyLoadable(() => import('../../components/back-to-top-link'), {
    fallback,
    ssr: false
})
const RecommendedAccessories = lazyLoadable(
    () => import('../../components/recommended-accessories'),
    {
        fallback,
        ssr: false
    }
)
const RecentlyViewed = lazyLoadable(() => import('./partials/recently-viewed'), {
    fallback,
    ssr: false
})
// used for content bellow the fold to be lazy loaded
const LazyCMSContent = lazyLoadable(async () => CMSContent, {
    fallback,
    ssr: false
})
const Fanreel = lazyLoadable(() => import('../../components/fanreel'), {
    fallback,
    ssr: false
})

// A poor-man's cache to reduce rerenders and fetches by caching the master product and reuse when variant changes
let lastProduct = null
const ProductDetail = ({
    category,
    product: masterProduct,
    promotions,
    variant: variantProduct,
    emotionalIntroContent,
    topContent,
    loadedOnServer
}) => {
    lastProduct = masterProduct
    const [masterProductWithCustomerPromotions, isMasterPromotionsLoading] =
        useProductWithCustomerPromotions(masterProduct, loadedOnServer)
    // cache the last product in the component to include the ssr obtained one
    const [variantProductWithCustomerPromotions, isVariantPromotionsLoading] =
        useProductWithCustomerPromotions(variantProduct, loadedOnServer)
    const isPromotionsLoading = isMasterPromotionsLoading || isVariantPromotionsLoading
    const product = useUnifiedProduct(
        masterProductWithCustomerPromotions,
        variantProductWithCustomerPromotions
    )
    const productAsList = useMemo(() => [product], [product])
    const isProductASet = isProductSet(product)
    const {headerWrapper} = useHeader()
    const isSubnavigationVisible = headerWrapper && !isProductASet
    const einstein = useEinstein()
    const [primaryCategory, setPrimaryCategory] = useState(category)
    const isLoading = product == null
    const {pdpContentEnabled, bvEnabled} = useOcapiSite()
    const {getProductPageJSONLDScript, getBreadcrumbListJSONLDScript} = useStructuredData()
    const productPageJSONLDScript = getProductPageJSONLDScript(product, primaryCategory?.name)
    const breadcrumbListJSONLDScript = getBreadcrumbListJSONLDScript(primaryCategory, product)
    useTrackViewPDP(product)
    useInteractionStudioPixel()
    const childProductRefs = React.useRef({})
    // Reviews section is not available for product sets, spare parts and engraving
    const isReviewSectionEnabled = useMemo(
        () => bvEnabled && product && hasBVProductRating(product),
        [bvEnabled, product]
    )
    // keep track if we are loading a new variant
    const [isVariantLoading, setVariantLoading] = useState(false)
    useEffect(() => {
        // always clear the flag when the variant changes
        setVariantLoading(false)
    }, [variantProduct])
    const onVariantChange = () => {
        setVariantLoading(true)
    }
    // keep track if we are loading a new master product
    const [isMasterLoading, setMasterLoading] = useState(false)
    useEffect(() => {
        // clear both matcher and variant on master change as the variant may not have changed (i.e. null)
        setMasterLoading(false)
        setVariantLoading(false)
    }, [masterProduct])
    const onMasterChange = () => {
        // both variant and master are loading when the master changes
        setVariantLoading(true)
        setMasterLoading(true)
    }
    useInitialDataLayer({
        pageType: 'Product Detail'
    })
    // This page uses the `primaryCategoryId` to retrieve the category data. This attribute
    // is only available on `master` products. Since a variation will be loaded once all the
    // attributes are selected (to get the correct inventory values), the category information
    // is overridden. This will allow us to keep the initial category around until a different
    // master product is loaded.
    useEffect(() => {
        if (category) {
            setPrimaryCategory(category)
        }
    }, [category])
    /**************** Wishlist ****************/
    // const wishlist = useWishlist()
    // // TODO: DRY this handler when intl provider is available globally
    // const handleAddToWishlist = async (quantity) => {
    //     try {
    //         await wishlist.createListItem({
    //             id: product.id,
    //             quantity
    //         })
    //         trackAddToWishlist(product, basket)
    //         toast({
    //             title: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {
    //                 quantity: 1
    //             }),
    //             action: (
    //                 // it would be better if we could use <Button as={Link}>
    //                 // but unfortunately the Link component is not compatible
    //                 // with Chakra Toast, since the ToastManager is rendered via portal
    //                 // and the toast doesn't have access to intl provider, which is a
    //                 // requirement of the Link component.
    //                 <Button variant="link" onClick={() => navigate('/account/wishlist')}>
    //                     {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
    //                 </Button>
    //             )
    //         })
    //     } catch {
    //         toast({
    //             title: formatMessage(API_ERROR_MESSAGE),
    //             status: 'error'
    //         })
    //     }
    // }
    /**************** Product Set Handlers ****************/
    // eslint-disable-next-line no-unused-vars
    const [productSetSelection, setProductSetSelection] = useState({})
    // TODO add this check of selected set products with
    // https://dept-de.atlassian.net/browse/STOK-6964
    // eslint-disable-next-line no-unused-vars
    const handleProductSetValidation = useCallback(() => {
        // Run validation for all child products. This will ensure the error
        // messages are shown.
        Object.values(childProductRefs.current).forEach(({validateOrderability}) => {
            validateOrderability({scrollErrorIntoView: false})
        })
        // Using ot state for which child products are selected, scroll to the first
        // one that isn't selected.
        const selectedProductIds = Object.keys(productSetSelection)
        const firstUnselectedProduct = product.setProducts.find(
            ({id}) => !selectedProductIds.includes(id)
        )
        if (firstUnselectedProduct) {
            // Get the reference to the product view and scroll to it.
            const {ref} = childProductRefs.current[firstUnselectedProduct.id]
            if (ref.scrollIntoView) {
                ref.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end'
                })
            }
            return false
        }
        return true
    }, [product, productSetSelection])
    // TODO add this add to cart handling for product sets with
    // https://dept-de.atlassian.net/browse/STOK-6964
    // eslint-disable-next-line no-unused-vars
    const handleProductSetAddToCart = () => {
        // Get all the selected products, and pass them to the addToCart handler which
        // accepts an array.
        // eslint-disable-next-line no-unused-vars
        const productSelectionValues = Object.values(productSetSelection)
        // return handleAddToCart(productSelectionValues)
    }
    /**************** Einstein ****************/
    const onSendViewProduct = useEffectEvent((product) => einstein.sendViewProduct(product))
    useEffect(() => {
        if (product) {
            if (product.type.set) {
                onSendViewProduct(product)
                const childrenProducts = product.setProducts
                childrenProducts.map((child) => {
                    onSendViewProduct(child)
                })
            } else {
                onSendViewProduct(product)
            }
        }
    }, [onSendViewProduct, product])
    /**************** Zoom modal ****************/
    const [zoomModal, setZoomModal] = useState()
    const zoomModalProps = useGenericModal()
    const showZoomModal = (state) => {
        setZoomModal(state)
        zoomModalProps.onOpen()
    }
    /* PDP Sections */
    const sections = {
        overview: useRef(),
        specifications: useRef(),
        accessories: useRef(),
        howToUse: useRef(),
        spareParts: useRef(),
        reviews: useRef()
    }

    const productQuickviewModalProps = useProductQuickviewModal()
    productQuickviewModalProps.onRecommendedProductClick = productQuickviewModalProps.onOpen

    // Hotspot
    const onHotspotClick = ({pid}) => {
        productQuickviewModalProps.onOpen(pid)
    }
    // When open notify me modal close quickview modal
    const onOpenNotifyMeModal = () => {
        productQuickviewModalProps.onClose()
    }
    // precompute if there are accessories
    const hasRecommendedAccessories = useMemo(
        () =>
            product?.recommendations?.filter(
                ({recommendationType}) => recommendationType.value === RECOMMENDED_ACCESSORIES_TYPE
            )?.length >= MINIMUN_NUM_OF_RECOMMENDED_PRODUCTS,
        [product?.recommendations]
    )

    // scroll to ratings section when clicking on ratings
    const onRatingsClick = () => {
        if (sections?.reviews?.current) {
            sections.reviews.current.scrollIntoView({
                behavior: 'smooth'
            })
        }
        trackRRClickStars('page top')
    }

    return (
        <CachedProductsProvider>
            <Container
                layerStyle="page"
                variant="no-padding"
                className="sf-product-detail-page"
                data-testid="product-details-page"
            >
                <Seo
                    title={masterProduct?.pageTitle}
                    description={masterProduct?.pageDescription}
                    keywords={masterProduct?.pageKeywords}
                />
                <Helmet>
                    <link rel="canonical" href={product?.c_canonicalUrl} />
                    {productPageJSONLDScript}
                    {breadcrumbListJSONLDScript}
                </Helmet>
                {isSubnavigationVisible &&
                    createPortal(
                        <PdpSubnavigation
                            isPromotionsLoading={isPromotionsLoading}
                            isVariantLoading={isVariantLoading}
                            product={product}
                            promotions={promotions}
                            sections={sections}
                        />,
                        headerWrapper
                    )}
                <Flex direction="column" alignItems="stretch">
                    {/* Emotional intro */}
                    {product?.c_emotionalIntro && pdpContentEnabled && (
                        <CMSContent
                            id={product.c_emotionalIntro}
                            content={emotionalIntroContent}
                            showError={false}
                        />
                    )}
                    {/* Top Content */}
                    {product?.c_pdpTopContent && pdpContentEnabled && (
                        <CMSContent
                            id={product.c_pdpTopContent}
                            content={topContent}
                            showError={false}
                        />
                    )}
                    {/* Cockpit */}
                    <Box
                        ref={sections.overview}
                        paddingTop={{
                            base: 4,
                            lg: 8
                        }}
                        paddingBottom={12}
                        px={{base: 0, lg: 8}}
                    >
                        {isLoading || !product ? (
                            <CockpitSkeleton />
                        ) : (
                            <>
                                {isProductASet ? (
                                    <ProductSetCockpit
                                        product={product}
                                        promotions={promotions}
                                        category={primaryCategory?.parentCategoryTree || []}
                                        isMasterLoading={isMasterLoading}
                                        isPromotionsLoading={isPromotionsLoading}
                                        isVariantLoading={isVariantLoading}
                                        isProductASet={isProductASet}
                                        onAddToCartClick={onOpenNotifyMeModal}
                                        showZoomModal={showZoomModal}
                                        onHotspotClick={onHotspotClick}
                                        onMasterChange={onMasterChange}
                                        onVariantChange={onVariantChange}
                                        onRatingsClick={onRatingsClick}
                                    />
                                ) : (
                                    <Cockpit
                                        product={product}
                                        promotions={promotions}
                                        category={primaryCategory?.parentCategoryTree || []}
                                        isMasterLoading={isMasterLoading}
                                        isPromotionsLoading={isPromotionsLoading}
                                        isVariantLoading={isVariantLoading}
                                        showZoomModal={showZoomModal}
                                        onAddToCartClick={onOpenNotifyMeModal}
                                        onHotspotClick={onHotspotClick}
                                        onMasterChange={onMasterChange}
                                        onVariantChange={onVariantChange}
                                        onRecommendedProductClick={
                                            productQuickviewModalProps.onOpen
                                        }
                                        onRatingsClick={onRatingsClick}
                                    />
                                )}
                            </>
                        )}
                    </Box>
                    {/* Mid Content */}
                    {product?.c_pdpMidContent && pdpContentEnabled && (
                        <LazyCMSContent id={product.c_pdpMidContent} showError={false} />
                    )}
                    {/* Accessory adder */}
                    <AccessoryAdder
                        product={product}
                        onRecommendedProductClick={productQuickviewModalProps.onOpen}
                    />
                    {/* Emotional gallery */}
                    <EmotionalGallery
                        imageGroups={product?.imageGroups}
                        selectedVariationAttributes={product?.variationValues}
                        extraCarouselImage={product?.c_extraCarouselImage}
                    />
                    {/* USP */}
                    <Box paddingTop={10} paddingBottom={12}>
                        <ProductUSP product={product} />
                    </Box>
                    {/* Bottom Content */}
                    {product?.c_pdpBottomContent && pdpContentEnabled && (
                        <LazyCMSContent id={product.c_pdpBottomContent} showError={false} />
                    )}
                    {/* Specification */}
                    <Box ref={sections.specifications} background="stokkeGray.bright">
                        <Specifications product={product}></Specifications>
                    </Box>
                    {/* Reco (Accessories) */}
                    {hasRecommendedAccessories && (
                        <RecommendedAccessories
                            ref={sections.accessories}
                            products={productAsList}
                            onRecommendedProductClick={productQuickviewModalProps.onOpen}
                        />
                    )}
                    {/* Breadcrumb */}
                    <Box py="2" px={{base: 4, xl: 8}}>
                        <Breadcrumb
                            categories={primaryCategory?.parentCategoryTree || []}
                            product={product?.name}
                        />
                    </Box>
                    {/* Bottom Content2 */}
                    {product?.c_pdpBottomContent2 && pdpContentEnabled && (
                        <LazyCMSContent id={product.c_pdpBottomContent2} showError={false} />
                    )}
                    {/* Social */}
                    {product?.c_curalate ? <Fanreel product={product} /> : null}
                    {/* FAQs */}
                    <AfterSales
                        product={product}
                        sections={sections}
                        onRecommendedProductClick={productQuickviewModalProps.onOpen}
                    />
                    {/* Reviews */}
                    {isReviewSectionEnabled && (
                        <ReviewOverview ref={sections.reviews} product={product} />
                    )}
                    {/* Reco */}
                    <ProductRecommendations
                        product={product}
                        onRecommendedProductClick={productQuickviewModalProps.onOpen}
                    />
                    {/* Recently viewed */}
                    <RecentlyViewed onRecommendedProductClick={productQuickviewModalProps.onOpen} />
                    {/* Back to top */}
                    <Box background="stokkeGray.bright" paddingTop={2} paddingBottom={12}>
                        <Flex justifyContent="center">
                            <BackToTopLink />
                        </Flex>
                    </Box>
                </Flex>
                <GenericModal {...zoomModalProps} size="full" colorScheme="white">
                    {/* zoom modal is loaded async client side for performance, but the generic modal is already available,
                    so user can open the modal and see placeholder while loading */}
                    <ZoomModal {...zoomModal} product={product} hide360={isProductASet} />
                </GenericModal>
                <ProductQuickviewModal {...productQuickviewModalProps} />
            </Container>
        </CachedProductsProvider>
    )
}
ProductDetail.getTemplateName = () => 'product-detail'
ProductDetail.shouldGetProps = ({previousLocation, location}) => {
    const previousParams = new URLSearchParams(previousLocation?.search || '')
    const params = new URLSearchParams(location.search)
    // If the product changed via the pathname or `pid` param, allow updated
    // data to be retrieved.
    if (
        previousLocation?.pathname !== location.pathname ||
        previousParams.get('pid') !== params.get('pid')
    ) {
        return true
    }
    // Get props if material of one of the products in the product set changed.
    for (const key of params.keys()) {
        const previousProductParams = new URLSearchParams(previousParams.get(key) || '')
        const productParams = new URLSearchParams(params.get(key) || '')
        if (previousProductParams.get('pid') !== productParams.get('pid')) {
            return true
        }
    }
    return false
}

ProductDetail.getProps = async ({res, params, location, api}) => {
    const {productId} = params
    let category, product, variant
    const urlParams = new URLSearchParams(location.search)
    product = await api.shopperProducts.getProduct({
        parameters: {
            id: urlParams.get('pid') || productId,
            allImages: true
        }
    })

    if (product.isError || !product.c_isOnline) {
        throw new HTTPNotFound('Product is not available')
    }

    const masterProductId = getMasterProductId(product)
    if (masterProductId != null) {
        variant = product
        // reuse the last product if it's the same
        if (lastProduct?.id === masterProductId) {
            product = lastProduct
        } else {
            product = await api.shopperProducts.getProduct({
                parameters: {
                    id: masterProductId,
                    allImages: true
                }
            })
        }
    }
    // If the product is a set - get all selected products from the url params and
    // replace new ones from the URL with old ones from the product set
    if (product?.type?.set) {
        let products = Object.fromEntries(
            product.setProducts.flatMap((setProduct) => [
                getMasterProductId(setProduct)
                    ? [
                          getMasterProductId(setProduct),
                          {
                              ...setProduct,
                              id: getMasterProductId(setProduct),
                              incomplete: true,
                              type: {master: true}
                          }
                      ]
                    : [],
                ...(setProduct.variants?.map((variant) => [
                    variant.productId,
                    {
                        ...variant,
                        id: variant.productId,
                        incomplete: true,
                        master: setProduct.master,
                        type: {variant: true}
                    }
                ]) || []),
                [setProduct.id, setProduct]
            ])
        )
        const ids = product.setProducts.map((setProduct, index) => {
            const productParams = new URLSearchParams(
                urlParams.get(setProduct.id + '_' + index) || ''
            )
            return productParams.get('pid') || setProduct.id
        })

        // when different material is selected the product is not in the setProducts array nor in the variants of those products
        // so we need to fetch it separately
        const missingProductIds = ids.filter((id) => !products[id])
        if (missingProductIds.length > 0) {
            const missingProducts = await api.shopperProducts.getProducts({
                parameters: {
                    ids: missingProductIds.join(','),
                    allImages: true
                }
            })
            products = {
                ...products,
                ...Object.fromEntries(
                    missingProducts.data.flatMap((setProduct) => [
                        getMasterProductId(setProduct)
                            ? [
                                  getMasterProductId(setProduct),
                                  {
                                      ...setProduct,
                                      id: getMasterProductId(setProduct),
                                      incomplete: true,
                                      type: {master: true}
                                  }
                              ]
                            : [],
                        ...(setProduct.variants?.map((variant) => [
                            variant.productId,
                            {
                                ...variant,
                                id: variant.productId,
                                incomplete: true,
                                master: setProduct.master,
                                type: {variant: true}
                            }
                        ]) || []),
                        [setProduct.id, setProduct]
                    ])
                )
            }
        }

        // list of selected set products by selecting the appropriate variant when needed
        const selectedSetProductIds = ids
            // map to products
            .map((id) =>
                isProductMaster(products[id]) ? getPreselectedVariant(products[id])?.productId : id
            )

        // list of all set product ids we need
        const requiredProductIds = [
            ...new Set(
                selectedSetProductIds
                    .flatMap((id) => [id, getMasterProductId(products[id])])
                    .filter(Boolean)
            )
        ]

        // list of incomplete products we need to fetch
        const incompleteProductIds = requiredProductIds.filter(
            (id) => products[id]?.incomplete ?? true
        )

        const [priceResp, productsResp] = await Promise.all([
            api.customObjects.getProductSetPrice({
                parameters: {
                    id: product.id,
                    selectedSetProducts: selectedSetProductIds
                }
            }),
            api.shopperProducts.getProducts({
                parameters: {
                    ids: incompleteProductIds.join(','),
                    allImages: true
                }
            })
        ])
        // merge the price response into the product set
        product = {...product, ...priceResp}
        // cache original set product ids by index
        const masterProductIds = product.setProducts.map((setProduct) => setProduct.id)
        // merge the newly fetched products into the product set
        products = {
            ...products,
            ...Object.fromEntries(productsResp.data.map((p) => [p.id, p]))
        }
        product.setProducts = selectedSetProductIds.map((id, index) => ({
            // use unifyProduct only if we have master & variant - maybe in future need to extend unifyProduct to handle
            // all types of products
            ...(getMasterProductId(products[id])
                ? unifyProduct(products[getMasterProductId(products[id])], products[id])
                : products[id]),
            masterId: masterProductIds[index]
        }))
    }
    if (!variant && isProductMaster(product)) {
        const pickedVariant = getPreselectedVariant(product)
        if (pickedVariant) {
            variant = await api.shopperProducts.getProduct({
                parameters: {
                    id: pickedVariant.productId,
                    allImages: true
                }
            })
        }
    }
    const unifiedProduct = unifyProduct(product, variant)
    const [emotionalIntroContent, topContent] = await Promise.all([
        unifiedProduct?.c_emotionalIntro &&
            preloadCmsContent(unifiedProduct.c_emotionalIntro, {api}),
        unifiedProduct?.c_pdpTopContent && preloadCmsContent(unifiedProduct.c_pdpTopContent, {api})
    ])
    if (product?.primaryCategoryId) {
        category = await api.shopperProducts.getCategory({
            parameters: {
                id: product?.primaryCategoryId,
                levels: 1
            }
        })
    }
    const promotionIds = unifiedProduct.productPromotions
        ?.filter(({promotionId}) => promotionId)
        ?.map(({promotionId}) => promotionId)
    const promotions = promotionIds
        ? (
              await api.shopperPromotions.getPromotions({
                  parameters: {ids: promotionIds.join(',')}
              })
          ).data
        : []
    // Set the `cache-control` header values similar to those on the product-list.
    if (res) {
        const cacheConfig = getConfig().app.cache
        if (cacheConfig?.pdp) {
            res.set('Cache-Control', cacheConfig.pdp)
        }
    }
    // The `commerce-isomorphic-sdk` package does not throw errors, so
    // we have to check the returned object type to inconsistencies.
    if (typeof product?.type === 'string') {
        throw new HTTPNotFound(product.detail)
    }
    if (typeof category?.type === 'string') {
        throw new HTTPNotFound(category.detail)
    }
    return {
        category,
        product,
        promotions,
        variant,
        emotionalIntroContent,
        topContent,
        loadedOnServer: isServer
    }
}

ProductDetail.propTypes = {
    /**
     * The category object used for breadcrumb construction.
     */
    category: PropTypes.object,
    /**
     * The simple product or master product object to be shown on the page
     */
    product: PropTypes.object,
    /**
     * Preloaded promotions for the product.
     */
    promotions: PropTypes.array,
    /**
     * The variant product object to be shown on the page if one is selected
     */
    variant: PropTypes.object,
    /**
     * The emotional intro component preloaded content.
     */
    emotionalIntroContent: PropTypes.object,
    /**
     * The emotional intro component preloaded content.
     */
    topContent: PropTypes.object,
    /**
     * The current state of `getProps` when running this value is `true`, otherwise it's
     * `false`. (Provided internally)
     */
    isLoading: PropTypes.bool,
    /**
     * The current react router match object. (Provided internally)
     */
    match: PropTypes.object,
    /**
     * Marks if the product data was loaded on server
     */
    loadedOnServer: PropTypes.bool
}

export default ProductDetail
